import './Designer.css';
import 'reactflow/dist/style.css';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactFlow, {
  addEdge,
  Controls,
  MiniMap,
  Background,
  ControlButton,
  Node,
  ReactFlowInstance,
  Connection,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  Edge,
  NodeChange,
  NodeRemoveChange,
  EdgeChange,
  EdgeRemoveChange,
} from 'reactflow';

import classNames from 'classnames';
import { v4 as generateUUID } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import { Designer } from '../../api/Designer';
import { generateItemFromPortInfo, getNodeInputEdge, getNodeId, NewNode } from '../../utils/DesignerUtils';

import AssetNode from './AssetNode';
import BatchServiceNode from './BatchServiceNode';
import StreamServiceNode from './StreamServiceNode';

import { inspectingItemTypeSelector, setInspectingItem } from '../../slices/inspectorSlice';
import { useDispatch, useSelector } from 'react-redux';
import { pushUsedItem, setUsedItems, applicationServicesSelector, updateUsedItem } from '../../slices/designerSlice';
import { cloneDeep } from 'lodash';
import { useToasts } from '../../contexts/toasts';
import { PortInfo, getSelectedColumns, setObjectType } from '../../utils';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { RootState } from '../../store';
import { UsedItem } from '../../models/generics';
import { BdaService } from '../../models/BdaService';
import { BdaDataset } from '../../models/BdaDataset';
import { BdaModel } from '../../models/BdaModel';
import { EventEmitter } from '../../utils/EventEmitter';

const nodeTypes = {
  dataset: AssetNode,
  model: AssetNode,
  batch: BatchServiceNode,
  stream: StreamServiceNode,
};

type Props = {
  showInspector?: boolean;
  onShowInspectorChange?: React.Dispatch<React.SetStateAction<boolean>>;
  showPalette?: boolean;
  onShowPaletteChange?: React.Dispatch<React.SetStateAction<boolean>>;
  darkBackground?: boolean;
  readOnly?: boolean;
  editMode?: boolean;
  initialNodes?: Node[];
  initialEdges?: Edge[];
  onElementSelected?: (el: any) => void;
}

export default function DesignerComponent({
  showInspector,
  onShowInspectorChange,
  showPalette,
  onShowPaletteChange,
  darkBackground = false,
  initialNodes,
  initialEdges,
  // onImportApplication,
  // onExportApplication,
  readOnly = false,
  editMode = false,
  onElementSelected,
}: Props) {
  console.log("[Designer] re-render");
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance>();
  const [showMinimap, setShowMinimap] = useState(true);

  const [cache, setCache] = useState<UsedItem<BdaService | BdaDataset | BdaModel>[]>([]);
  const [initialized, setInitialized] = useState(false);

  const inspectingItemType = useSelector(inspectingItemTypeSelector);
  const usedItems = useSelector((state: RootState) => state.designer.usedItems);
  const applicationServices = useSelector(applicationServicesSelector);
  const availableStorages = useSelector((state: RootState) => state.designer.availableStorages);
  const defaultStorage = useSelector((state: RootState) => state.designer.defaultStorage);

  const dispatch = useDispatch();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges || []);

  useEffect(() => {
    if (usedItems) console.log(usedItems);
    // eslint-disable-next-line
  }, [usedItems]);

  useEffect(() => {
    if (reactFlowInstance && readOnly && nodes.length > 0) {
      reactFlowInstance.fitView({ padding: 0.125 });
    }
  }, [reactFlowInstance, readOnly, nodes]);

  useEffect(() => {
    EventEmitter.subscribe('selected-port', (selectedPort: PortInfo) => {
      const itemInfo = generateItemFromPortInfo(selectedPort, edges, usedItems);
      dispatch(setInspectingItem(itemInfo));
    });

    return () => EventEmitter.unsubscribe('selected-port');
  }, [dispatch, edges, usedItems]);

  useEffect(() => {
    if (editMode) {
      showLoader();
      if (availableStorages && !initialized) {
        const promises = nodes.map(node => updateUsedItems(node as NewNode));
        Promise.all(promises).then(() => setInitialized(true));
      }
    }
    // eslint-disable-next-line
  }, [availableStorages, initialized]);

  useEffect(() => {
    if (initialized) {
      nodes
        .filter(node => node.type !== "dataset" && node.type !== "model")
        .forEach(node => {
          const targetItem = cloneDeep(usedItems.find(item => item.ref === node.id)) as UsedItem<BdaService>;
          const targetService = applicationServices.find(service => service.ref === node.id)!;
          targetItem.properties = cloneDeep(targetService.properties);

          node.data.inputs?.forEach((input: any) => {
            const params = getNodeInputEdge(node, edges, input.id)!;
            const sourceItem = cloneDeep(usedItems.find(item => item.ref === params?.source));

            if (!sourceItem) return;

            switch (sourceItem.type) {
              case 'batch':
              default:
                // handling service source might not be needed
                // bda-parser should take charge of everything
                if (targetItem.type === "batch" && input.type === "dataset") {
                  targetItem.cascade = true;
                  targetItem.inputs!.find(targetInput => targetInput.id === params.targetHandle && targetInput.type === "dataset").dataset = {
                    columns: targetService!.properties.find(prop => prop.key === "input-columns")?.value || "*",
                  };
                }
                break;

              case 'dataset':
                targetItem.properties?.forEach(prop => {
                  if (prop.key === params.targetHandle) {
                    prop.dataset = { id: +sourceItem.id! };
                  }
                });
                const selectedColumns = getSelectedColumns(targetService, params.targetHandle as string) || [];
                targetItem.inputs!.find(input => input.id === params.targetHandle).dataset = {
                  id: sourceItem.id,
                  name: sourceItem.name,
                  type: sourceItem.datasetFileType.type,
                  columns: sourceItem.columns?.map((col: any) => ({ ...col, selected: selectedColumns.includes(col.name) })),
                };
                break;

              case 'model':
                targetItem.properties?.forEach(prop => {
                  if (prop.key === 'input-model') {
                    prop.value = sourceItem.location;
                    const modelClone = structuredClone(sourceItem) as any;
                    delete modelClone.type;
                    delete modelClone.ref;
                    prop.model = modelClone;
                  }
                })
                break;
            }
          });
          if (targetItem.type === "batch") {
            targetItem.outputs!.forEach(output => {
              const property = targetService.properties.find(prop => prop.key === output.id);
              if (property) output.targetStorage = property.dataset?.datasource?.id || property.model?.datasource?.id || null;
            });
          }
          dispatch(updateUsedItem(targetItem));
        });
      hideLoader();
    }
    // eslint-disable-next-line
  }, [initialized])

  const togglePalette = useCallback(() => onShowPaletteChange?.(!showPalette), [showPalette, onShowPaletteChange]);
  const toggleInspector = useCallback(() => onShowInspectorChange?.(!showInspector), [showInspector, onShowInspectorChange]);

  const onConnect = (params: Connection) => { // {source, sourceHandle, target, targetHandle}
    setEdges((eds) => addEdge({ ...params, markerEnd: 'arrow' }, eds));

    const sourceItem = usedItems.find(item => item.ref === params.source);
    const targetItem = cloneDeep(usedItems.find(item => item.ref === params.target)) as UsedItem<BdaService>;

    if (!sourceItem) return;

    switch (sourceItem.type) {
      case 'batch':
      default:
        // handling service source might not be needed
        // bda-parser should take charge of everything
        if (targetItem.type === "batch" && params.targetHandle!.includes("dataset")) {
          targetItem.cascade = true;
          targetItem.inputs!.find(input => input.id === params.targetHandle).dataset = {
            columns: "*",
          };
        }
        break;

      case 'dataset':
        targetItem.properties?.forEach(prop => {
          if (prop.key === 'input-dataset') {
            prop.dataset = { id: sourceItem.id };
          }
        });
        const targetAsset = targetItem.inputs!.find(input => input.id === params.targetHandle);
        targetAsset.dataset = {
          id: sourceItem.id,
          name: sourceItem.name,
          type: sourceItem.datasetFileType.type,
          columns: sourceItem.columns?.map((col: any) => {
            const isIncluded = targetAsset.acceptedTypes.includes("ANY") || targetAsset.acceptedTypes.includes(col.type.toUpperCase());
            return { ...col, selected: isIncluded, disabled: !isIncluded };
          }),
        };
        break;

      case 'model':
        targetItem.properties?.forEach(prop => {
          if (prop.key === 'input-model') {
            prop.value = sourceItem.location;
            const modelClone = { ...sourceItem } as any;
            delete modelClone.type;
            delete modelClone.ref;
            prop.model = modelClone;
          }
        })
        break;
    }

    const updatedItems = cloneDeep(usedItems);
    const index = usedItems.findIndex(item => item.ref === targetItem.ref);
    updatedItems[index] = targetItem;
    dispatch(setUsedItems(updatedItems));
  };

  const onPaneContextMenu = (event: React.MouseEvent) => console.log('onPaneContextMenu', event);

  const onPaneClick = (event: React.MouseEvent) => {
    if (!readOnly) {
      dispatch(setInspectingItem(null));
    }
  };

  const handleNodesChange = (changes: NodeChange[]) => {
    onNodesChange(changes);
    // check usedItems to remove
    const refsToRemove = changes.filter(change => change.type === 'remove').map(change => (change as NodeRemoveChange).id);
    if (refsToRemove.length > 0) {
      const newItems = usedItems.filter(item => !refsToRemove.includes(item.ref!));
      dispatch(setUsedItems(newItems));
    }
  }

  const handleEdgesChange = (changes: EdgeChange[]) => {
    // remove datasets and models from service inputs
    const idsToRemove = changes.filter(change => change.type === 'remove').map(change => (change as EdgeRemoveChange).id);
    if (idsToRemove.length > 0) {
      const edgesToRemove = edges.filter(edge => idsToRemove.includes(edge.id));
      const newItems = cloneDeep(usedItems);

      edgesToRemove.forEach(edge => {
        const found = newItems.findIndex(item => item.ref === edge.target);
        if (found > -1) {
          newItems[found].inputs!.forEach(input => {
            if (input.id === edge.targetHandle) {
              delete input.dataset;
              delete input.model;
            }
          });
        }
      });
      dispatch(setUsedItems(newItems));
    }
    onEdgesChange(changes);
  }

  const onElementClick = (event: React.MouseEvent, element: Node | Edge) => {
    if (!readOnly) {
      if (!showInspector) onShowInspectorChange?.(true);
      retrieveElementDetail(element);
    }
    else onElementSelected?.(element);
  };

  const onInit = (_reactFlowInstance: ReactFlowInstance) => setReactFlowInstance(_reactFlowInstance);

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const retrieveElementDetail = (element: Node | Edge) => {
    const found = usedItems.find(item => item.ref === element.id);
    dispatch(setInspectingItem(found));
  }

  const updateUsedItems = async (node: NewNode) => {
    const id = getNodeId(node);
    const found = cache.find((element) => element.id === id);
    let element: UsedItem<any>;

    try {
      if (found) {
        element = cloneDeep(found);
      } else {
        const promise = node.type === 'dataset'
          ? Designer.getDataset(id)
          : node.type === 'model'
            ? Designer.getModel(id)
            : Designer.getService(id);

        const { data } = await promise;
        element = cloneDeep(data) as UsedItem<any>;
      }

      if (node.type === 'dataset' && setObjectType<BdaDataset>(element) && element.datasetFileType!.type === 'table') {
        element.columns = element.datasetFileType!.columns || [];
      }
      else if (node.type === "batch" && setObjectType<BdaService>(element)) {
        element.inputs = cloneDeep(node.data.inputs);
        const outputDatasets = (node.data.outputs as any[])?.filter(out => out.id.includes("output-dataset"));
        const outputModels = (node.data.outputs as any[])?.filter(out => out.id.includes("output-model"));

        if (outputDatasets && outputDatasets.length > 0) {
          outputDatasets.forEach(out => {
            if (out.targetStorage !== undefined) return;
            if (out.datasetMode === 'streaming') out.targetStorage = availableStorages!.find(storage => storage.default && storage.type === 'messageBroker')?.id;
            else out.targetStorage = defaultStorage || availableStorages!.find(storage => storage.default)?.id;
          });
        }
        if (outputModels && outputModels.length > 0) {
          outputModels.forEach(out => {
            if (out.targetStorage !== undefined) return;
            if (out.datasetMode === 'streaming') out.targetStorage = availableStorages!.find(storage => storage.default && storage.type === 'messageBroker')?.id;
            else out.targetStorage = defaultStorage || availableStorages!.find(storage => storage.default)?.id;
          });
        }
        element.outputs = [...outputDatasets, ...outputModels];
      }
      dispatch(pushUsedItem({ ...element, label: element.name, ref: node.id, type: node.type }));
      setCache((cache) => cache.concat(element));
    } catch (err) {
      console.log(err);
      showToast('api-error', 'Node error', `Could not get '${node.data.text}' info`, 'danger')
    }
  }

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    // const type = event.dataTransfer.getData('application/reactflow');
    const id = event.dataTransfer.getData('application/node-id');
    const name = event.dataTransfer.getData('application/node-name');
    const type = event.dataTransfer.getData('application/node-category');

    const position = reactFlowInstance!.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    var newNode: NewNode;
    const uuid = generateUUID();
    if (type === 'batch' || type === 'stream') {
      const label = event.dataTransfer.getData('application/node-label');
      const mode = event.dataTransfer.getData('application/node-mode');
      const version = event.dataTransfer.getData('application/node-version');
      const owner = event.dataTransfer.getData('application/node-owner');
      const inputs = JSON.parse(event.dataTransfer.getData('application/node-inputs'));
      const outputs = JSON.parse(event.dataTransfer.getData('application/node-outputs'));

      newNode = {
        id: `${id}__${uuid}`,
        type,
        position,
        data: { text: name, name, label, version, owner, inputs, outputs, mode },
      };
    }
    else newNode = {
      id: `${id}__${uuid}`,
      type,
      position,
      data: { text: name },
    };

    if (type === "model") {
      const location = event.dataTransfer.getData('application/node-model-location');
      const trailing = event.dataTransfer.getData('application/node-trailing');

      newNode.data.trailing = trailing;
      newNode.data.model = {
        location,
      };
    }

    setNodes(nodes => nodes.concat(newNode));
    updateUsedItems(newNode);
  };

  return (
    <div className={classNames('designer-container', { readonly: readOnly })} ref={reactFlowWrapper}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        onInit={onInit}
        onDrop={onDrop}
        onDragOver={onDragOver}
        snapToGrid={true}
        snapGrid={[30, 30]}
        onNodeClick={onElementClick}
        onEdgeClick={onElementClick}
        onPaneClick={onPaneClick}
        onPaneContextMenu={onPaneContextMenu}
        nodeTypes={nodeTypes}
        selectNodesOnDrag={false}
        nodesConnectable={!readOnly}
        nodesDraggable={!readOnly}
        className={classNames({ "dark-background": darkBackground })}
      >
        <Controls className={classNames({ 'palette-open': showPalette })} showInteractive={!readOnly}>
          {!readOnly && (
            <>
              <ControlButton onClick={togglePalette}>
                <FontAwesomeIcon icon={solid('bars')} />
              </ControlButton>
              <ControlButton onClick={toggleInspector}>
                <FontAwesomeIcon icon={showInspector ? solid('eye-slash') : solid('eye')} />
              </ControlButton>
              {/* <ControlButton onClick={() => onImportApplication()}>
                <FontAwesomeIcon icon={solid('file-import')} />
              </ControlButton>
              <ControlButton onClick={() => onExportApplication()}>
                <FontAwesomeIcon icon={solid('file-export')} />
              </ControlButton> */}
            </>
          )}
          <ControlButton onClick={() => setShowMinimap(!showMinimap)}>
            <FontAwesomeIcon icon={solid('location-arrow')} />
          </ControlButton>
        </Controls>

        {showMinimap && (
          <MiniMap
            nodeStrokeColor={(n) => {
              if (n.type === 'dataset') return '#5d8';
              if (n.type === 'model') return '#78abcc';
              if (n.type === 'batch') return '#fa801b';
              return 'transparent';
            }}
            nodeColor={(n) => {
              if (n.type === 'dataset') return '#55dd8855';
              if (n.type === 'model') return '#78abcc55';
              if (n.type === 'batch') return '#fa801b55';
              if (n.data.mode === 'SOURCE') return '#14b8a655';
              if (n.data.mode === 'PROCESSOR') return '#f59e0b55';
              if (n.data.mode === 'SINK') return '#ec489955'
              return '#878787';
            }}
            nodeBorderRadius={2}
            className={classNames({ 'inspector-open': showInspector, 'inspecting-dataset': inspectingItemType?.includes('input-dataset') })}
          />
        )}

        <Background color={darkBackground ? "#bbb" : "#eee"} variant={BackgroundVariant.Lines} gap={15} />
      </ReactFlow>
    </div>
  );
};